import { Bell, Bot, ExternalLink, Plug, Plus, ShieldCheck, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../api/envelope';
import type { Inbox, ManagedInbox } from '../../api/types';
import {
  channelHealth,
  healthCounts,
  identityLine,
  isManagedInbox,
  providerLine,
  reconnectViaOauth,
} from '../../lib/channelMeta';
import { RefreshButton } from '../../components/RefreshButton';
import { SmsDefaultForm } from '../../components/SmsDefaultForm';
import { AI_LABEL, CAMPAIGN_HINT, LEVEL_LABEL } from '../../lib/accessLevels';
import { channelMetaForInbox, INBOX_ICONS, inboxIconKey } from '../../lib/commsMeta';
import { useTimeFormat } from '../../lib/tenantTime';
import { launchReconnect } from '../../oauth/launch';
import { ShowChannelBar } from './channelBar';
import { useComms } from './context';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { NotificationSettingsCard } from './NotificationSettingsCard';
import { TeamAccessCard } from './TeamAccessCard';
import { ChannelCardsSkeleton } from './Skeletons';
import { useConfigNav } from './useConfigNav';

/**
 * The extra status line under the provider, per channel type.
 *
 * Every line here is drawn from the channel's SETTINGS, which the server sends
 * only to whoever manages the channel — so a channel the reader merely works
 * says who it belongs to instead. Reading through the redaction is what crashed
 * this screen for an agent the moment a website channel was granted to them.
 */
function metaLine(inbox: ManagedInbox, fmtDateTime: (iso: string | null) => string): string {
  switch (inbox.channelType) {
    case 'email': {
      const c = inbox.channel;
      const parts = [];
      parts.push(c.verifiedForSending ? 'Verified for sending' : 'Not verified for sending');
      if (c.imapEnabled) parts.push('IMAP sync on');
      return parts.join(' · ');
    }
    case 'ringcentral': {
      const c = inbox.channel;
      return [
        c.connected ? 'Connected' : 'Not connected',
        c.webhookSubscribed ? 'inbound webhook live' : 'webhook missing',
        'voice + recordings via call-log sync',
      ].join(' · ');
    }
    case 'whatsapp': {
      const c = inbox.channel;
      const synced = c.templatesSyncedAt ? ` · synced ${fmtDateTime(c.templatesSyncedAt)}` : '';
      return `${c.templateCount} approved template${c.templateCount === 1 ? '' : 's'}${synced}`;
    }
    case 'telegram':
      return 'Webhook registered · private chats, media, buttons';
    case 'facebook': {
      const c = inbox.channel;
      return [
        c.webhookRegisteredAt ? 'Page subscribed' : 'Page subscription missing',
        c.instagramUsername ? `Instagram @${c.instagramUsername} linked` : 'no Instagram linked',
        c.sessionWindowHours === 168 ? '7-day reply window' : '24-hour reply window',
      ].join(' · ');
    }
    case 'instagram': {
      const c = inbox.channel;
      const via =
        c.source === 'facebook_page'
          ? 'via its Facebook Page'
          : c.source === 'instagram_login'
            ? 'signed in with Instagram'
            : 'access token';
      return [
        via,
        c.webhookRegisteredAt ? 'subscribed' : 'subscription missing',
        c.tokenExpiresAt ? `token renews before ${fmtDateTime(c.tokenExpiresAt)}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
    }
    case 'website': {
      const c = inbox.channel;
      return [
        'Chat widget on your site',
        c.allowedOrigins.length
          ? `${c.allowedOrigins.length} allowed site${c.allowedOrigins.length === 1 ? '' : 's'}`
          : 'any site',
      ].join(' · ');
    }
    case 'twilio': {
      const c = inbox.channel;
      return [
        c.medium === 'whatsapp' ? 'WhatsApp sender' : 'SMS/MMS',
        c.voiceEnabled ? 'browser voice enabled' : 'voice off',
      ].join(' · ');
    }
  }
}

/** What the card says instead when the settings are not this reader's to see. */
const NOT_YOURS = 'Settings are managed by an administrator';

/**
 * One screen per question, instead of one scroll for all three.
 *
 * The tab had grown to four unrelated cards stacked vertically — channels, the
 * reader's own access, the whole team's access, notifications — so the two an
 * agent actually opens it for sat below the fold on every visit, behind a grid
 * whose length is the tenant's channel count. A rail names the three questions
 * and keeps each answer a click away rather than a scroll.
 *
 * Deliberately NOT in the URL. `?tab=` and `?c=` are the dashboard's embed
 * protocol (console/README.md); a section is a place inside one screen, not a
 * destination anything links to, and growing that contract for a local toggle
 * is how a protocol ends up with parameters nobody can remove later.
 */
type Section = 'channels' | 'access' | 'notifications';

const SECTIONS: { key: Section; label: string; icon: typeof Plug; hint: string }[] = [
  { key: 'channels', label: 'Channels', icon: Plug, hint: 'What is connected, and its health' },
  {
    key: 'access',
    label: 'Users & access',
    icon: ShieldCheck,
    hint: 'Who may do what, per person and per role',
  },
  { key: 'notifications', label: 'Notifications', icon: Bell, hint: 'What reaches you, and where' },
];

/**
 * The way out of the frame, and the answer to "where do I set up the chatbot?".
 *
 * Embedded, this module is a panel inside the dispatch dashboard: conversations
 * get whatever room the frame has, and the console's other sections — Chatbot,
 * AI — have no tab here at all. This card is the only thing on the screen that
 * says where they are, and `useConfigNav` pops the console open in a new tab
 * carrying a one-time sign-in code, the same way a channel's own Manage screen
 * and Connect already do.
 *
 * Standalone there is nothing to pop out to — the page IS full size — so the
 * card keeps only the half that is still true and points at the tabs above it.
 */
function OpenConsoleCard() {
  const { source } = useComms();
  const openConfig = useConfigNav();

  if (source.embedded) {
    return (
      <div className="cset-popout">
        <div className="cset-popout-icon">
          <ExternalLink size={16} />
        </div>
        <div className="cset-popout-main">
          <div className="cset-popout-title">Open Communications full size</div>
          <div className="cset-popout-sub">
            The console gives conversations the whole window — and it is where the{' '}
            <b>Chatbot</b> and <b>AI</b> settings are configured. Opens in a new tab.
          </div>
        </div>
        <button
          type="button"
          className="comms-tab-action primary"
          onClick={() => openConfig('/comms')}
        >
          <ExternalLink size={14} />
          Open full size
        </button>
      </div>
    );
  }

  return (
    <div className="cset-popout">
      <div className="cset-popout-icon">
        <Sparkles size={16} />
      </div>
      <div className="cset-popout-main">
        <div className="cset-popout-title">Chatbot and AI</div>
        <div className="cset-popout-sub">
          The assistant that answers first, and the model behind AI drafting, have their own
          tabs at the top of the console.
        </div>
      </div>
      <div className="cset-popout-actions">
        <button type="button" className="comms-tab-action" onClick={() => openConfig('/bot')}>
          <Bot size={14} />
          Chatbot
        </button>
        <button type="button" className="comms-tab-action" onClick={() => openConfig('/ai')}>
          <Sparkles size={14} />
          AI
        </button>
      </div>
    </div>
  );
}

/**
 * Channel settings: the design's connected-channels grid over the real
 * inboxes, plus the truthful permission matrix (dispatch permission slugs, not
 * the design's named roles).
 */
export function SettingsView() {
  const { fmtDateTime } = useTimeFormat();
  const { inboxes, inboxesLoading, inboxesRefreshing, reloadInboxes, source, capabilities } =
    useComms();
  const me = capabilities;
  // Their level per channel, worded once — "Reservations: Write" reads as an
  // answer where a table of slugs read as a specification.
  const myChannels = (me?.access.channels ?? []).map((grant) => {
    const inbox = inboxes.find((row) => row.id === grant.id);
    const calls = grant.canCall ? ' + calls' : '';
    return `${inbox?.name ?? `#${grant.id}`}: ${LEVEL_LABEL[grant.level]}${calls}`;
  });
  const openConfig = useConfigNav();
  const [section, setSection] = useState<Section>('channels');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const counts = healthCounts(inboxes);
  // See ConversationList: `inboxesRefreshing` is held past the response so a
  // fast refresh still reads as one.
  const busy = inboxesLoading || inboxesRefreshing;

  const reconnect = async (inbox: Inbox) => {
    setError(null);
    // Embedded, or a credentials channel: the manage screen handles it. The
    // OAuth redirect leg must never run inside the iframe (providers refuse
    // to render framed), so embedded mode always goes to a new tab.
    if (source.embedded || !reconnectViaOauth(inbox)) {
      openConfig(`/inboxes/${inbox.id}`);
      return;
    }
    setBusyId(inbox.id);
    try {
      await launchReconnect(inbox); // full-page redirect on success
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not start the reconnect.');
      setBusyId(null);
    }
  };

  // Team access is an administrator's screen and answers 1036 to anyone else, so
  // showing the card to an agent would put a permission error behind a rail item
  // they were invited to click. Absent capabilities means UNKNOWN, not "no" — the
  // rule every other helper here follows — so it renders and the server answers.
  const canSeeTeam = !me || me.access.isRoot;

  return (
    <div className="cset">
      {/* Above the pop-out card, which is the first thing on this tab. The
          dashboard's Settings shortcut opens it with the channel bar folded. */}
      <ShowChannelBar />
      <OpenConsoleCard />
      {/* The pop-out card is about the console, not about this tab. Without a
          rule under it the rail below reads as its third line. */}
      <div className="cset-divider" role="presentation" />

      <div className="cset-split">
        <nav className="cset-nav" aria-label="Settings sections">
          {SECTIONS.map(({ key, label, icon: Icon, hint }) => (
            <button
              key={key}
              type="button"
              className={`cset-navitem${section === key ? ' is-on' : ''}`}
              // The screenshot hook (console/scripts/capture-console.mjs): every
              // shot below the channels grid now sits behind a rail click, and a
              // named section is far steadier to aim at than nth-child.
              data-section={key}
              aria-current={section === key ? 'page' : undefined}
              onClick={() => setSection(key)}
            >
              <Icon size={14} />
              <span className="cset-navitem-main">
                <span className="cset-navitem-label">{label}</span>
                <span className="cset-navitem-hint">{hint}</span>
              </span>
            </button>
          ))}
        </nav>

        <div className="cset-pane">
          {section === 'channels' && (
            <>
              <div className="cset-head">
                <span className="cset-title">Connected channels</span>
                {counts.connected > 0 && (
                  <span className="cchip cchip-green">{counts.connected} connected</span>
                )}
                {counts.attention > 0 && (
                  <span className="cchip cchip-orange">{counts.attention} needs attention</span>
                )}
                <div style={{ flex: 1 }} />
                <RefreshButton
                  className="cbtn"
                  size={13}
                  title="Refresh channel status"
                  refreshing={inboxesRefreshing}
                  busy={inboxesLoading}
                  onClick={() => reloadInboxes()}
                />
                <button
                  type="button"
                  className="comms-tab-action primary"
                  onClick={() => openConfig('/connect')}
                >
                  <Plus size={14} />
                  Connect a channel
                </button>
              </div>

              {error && <div className="comms-error-note">{error}</div>}
              {/* Saves in place, like Team access. Only Reconnect leaves for a new
                  tab, as every manage screen does from here. */}
              <div className="cset-sms-default">
                <SmsDefaultForm
                  inboxes={inboxes}
                  canChange={me ? me.access.features.connectChannels : true}
                  onOpenInbox={(id) => openConfig(`/inboxes/${id}`)}
                  reload={() => reloadInboxes()}
                />
              </div>
              {/* Always reserve the bar's 2px so it appearing never nudges the grid. */}
              {inboxesRefreshing ? <div className="comms-progress" /> : <div className="comms-progress-slot" />}

              <div className="cset-grid" aria-busy={busy}>
                {inboxesLoading && inboxes.length === 0 && <ChannelCardsSkeleton />}
                {inboxes.map((inbox) => {
                  const meta = channelMetaForInbox(inbox);
                  const Icon = INBOX_ICONS[inboxIconKey(inbox)];
                  const healthy = channelHealth(inbox) === 'connected';
                  const mine = isManagedInbox(inbox);
                  return (
                    <div key={inbox.id} className="cset-card">
                      <div className="cset-card-icon" style={{ background: meta.muted }}>
                        <Icon size={18} style={{ color: meta.color }} />
                      </div>
                      <div className="cset-card-main">
                        <div className="cset-card-toprow">
                          <span className="cset-card-title">{identityLine(inbox)}</span>
                          <span
                            className="cset-card-state"
                            style={
                              healthy
                                ? { background: 'var(--c-green-muted)', color: 'var(--c-green)' }
                                : { background: 'var(--c-orange-muted)', color: 'var(--c-orange)' }
                            }
                          >
                            {healthy ? 'Connected' : 'Needs re-auth'}
                          </span>
                          {inbox.defaultSmsSender && (
                            <span
                              className="cset-card-state"
                              style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
                            >
                              Default SMS
                            </span>
                          )}
                        </div>
                        <div className="cset-card-sub">
                          {providerLine(inbox)} · {inbox.name}
                        </div>
                        <div className="cset-card-meta">
                          {mine ? metaLine(inbox, fmtDateTime) : NOT_YOURS}
                        </div>
                      </div>
                      {/* No action on a channel this reader only works: every control on
                          the manage screen, reconnect included, needs Manage on it. */}
                      {!mine ? null : healthy ? (
                        <button
                          type="button"
                          className="cset-card-action"
                          onClick={() => openConfig(`/inboxes/${inbox.id}`)}
                        >
                          Manage
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="cset-card-action warn"
                          disabled={busyId === inbox.id}
                          onClick={() => void reconnect(inbox)}
                        >
                          {busyId === inbox.id ? 'Opening…' : 'Reconnect'}
                        </button>
                      )}
                    </div>
                  );
                })}
                {inboxes.length === 0 && !inboxesLoading && (
                  <div className="ctable-empty" style={{ gridColumn: '1 / -1' }}>
                    No channels connected yet — start with <b>Connect a channel</b>.
                  </div>
                )}
              </div>
            </>
          )}

          {section === 'access' && (
            <>
              {/*
                What the reader's OWN access is, rather than a table of slugs. The
                card this replaced listed crm_all / crm_send / crm_config against
                four actions, which stopped being true when dispatch collapsed to
                one permission — and was never the question anyone opened Settings
                with. `.cset-matrix` is the screenshot hook (capture-console.mjs).
                It leads this section on purpose: it is the only half an agent who
                is not an administrator can be shown, and it is the half that
                answers the question they came with.
              */}
              <div className="cset-matrix">
                <div className="cset-matrix-title">Your access</div>
                <div className="cset-matrix-rows">
                  <div className="cset-matrix-row">
                    <span className="cset-matrix-what">Channels</span>
                    <span className="cset-matrix-who">
                      {me?.access.isRoot ? (
                        <>
                          <b>Manage</b> on every channel — you are an administrator
                        </>
                      ) : (
                        <>
                          {/* No capabilities yet means UNKNOWN, not "nothing" — every
                              other helper stays permissive in that state, and this card
                              must not be the one that tells you you have no access. */}
                          {!me
                            ? 'Checking…'
                            : myChannels.length
                              ? myChannels.map((line, at) => (
                                  <span key={line}>
                                    {at > 0 && ' · '}
                                    {line}
                                  </span>
                                ))
                              : 'No channels yet'}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="cset-matrix-row">
                    <span className="cset-matrix-what">Campaigns</span>
                    <span className="cset-matrix-who">
                      {CAMPAIGN_HINT[me?.access.features.campaigns ?? 'full']}
                    </span>
                  </div>
                  <div className="cset-matrix-row">
                    <span className="cset-matrix-what">Chatbot and AI</span>
                    <span className="cset-matrix-who">
                      Chatbot: <b>{me?.access.features.bot === 'manage' ? 'yes' : 'no'}</b> · AI:{' '}
                      <b>{AI_LABEL[me?.access.features.ai ?? 'use']}</b>
                    </span>
                  </div>
                  <div className="cset-matrix-row">
                    <span className="cset-matrix-what">Connect a channel</span>
                    <span className="cset-matrix-who">
                      {me?.access.features.connectChannels
                        ? 'Yes — whatever you connect, you manage'
                        : 'No — ask an administrator'}
                    </span>
                  </div>
                </div>
                <div className="cmg-hint">
                  Dispatch decides one thing: whether you can open the CRM at all. Everything
                  above is set in this section — one level per person per channel, plus the
                  permissions that are not about a channel.
                </div>
              </div>

              {canSeeTeam ? (
                <TeamAccessCard />
              ) : (
                <div className="cset-matrix">
                  <div className="cset-matrix-title">Everyone else&rsquo;s access</div>
                  <div className="cmg-hint">
                    Set by an administrator, for the whole team at once. If something above
                    is not what you need, they are who to ask.
                  </div>
                </div>
              )}
            </>
          )}

          {/* Its own boundary as well as the pane's: a bad value in the reach
              summary took the whole of Communications with it, and the rest of
              this screen — channels, access, the team — was never at fault. */}
          {section === 'notifications' && (
            <ErrorBoundary label="Notifications">
              <NotificationSettingsCard />
            </ErrorBoundary>
          )}
        </div>
      </div>
    </div>
  );
}
